import { useEffect, useRef, useCallback, useState } from "react";
import { HubConnectionState } from "@microsoft/signalr";
import { getSharedConnection } from "../signalrConnection";

interface OfferPresenceEvent {
  serviceRequestId: string;
  preparingCount: number;
}

/**
 * Subscribes to OfferPresenceUpdated SignalR events.
 * Returns a map of serviceRequestId -> preparingCount.
 *
 * Usage:
 *   const presenceCounts = useOfferPresence();
 *   const preparing = presenceCounts[serviceRequestId] || 0;
 */
const useOfferPresence = (): Record<string, number> => {
  const [presenceCounts, setPresenceCounts] = useState<Record<string, number>>({});
  const countsRef = useRef<Record<string, number>>({});

  const handler = useCallback((event: OfferPresenceEvent) => {
    const updated = { ...countsRef.current };
    if (event.preparingCount > 0) {
      updated[event.serviceRequestId] = event.preparingCount;
    } else {
      delete updated[event.serviceRequestId];
    }
    countsRef.current = updated;
    setPresenceCounts(updated);
  }, []);

  useEffect(() => {
    const connection = getSharedConnection();
    if (connection && connection.state === HubConnectionState.Connected) {
      connection.on("OfferPresenceUpdated", handler);
    }

    // Poll for connection (same pattern as useDataRefresh)
    let pollInterval: ReturnType<typeof setInterval> | null = null;
    let subscribed = false;

    if (!connection || connection.state !== HubConnectionState.Connected) {
      pollInterval = setInterval(() => {
        const conn = getSharedConnection();
        if (conn && conn.state === HubConnectionState.Connected && !subscribed) {
          conn.on("OfferPresenceUpdated", handler);
          subscribed = true;
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }
        }
      }, 500);
    } else {
      subscribed = true;
    }

    return () => {
      if (pollInterval) clearInterval(pollInterval);
      const conn = getSharedConnection();
      if (conn) {
        conn.off("OfferPresenceUpdated", handler);
      }
    };
  }, [handler]);

  return presenceCounts;
};

export default useOfferPresence;
